import { Component, Input } from '@angular/core';
import { agreementScheduledPayments } from '../agreement-scheduled-payments';
import { agreementPackageName } from '../agreement-package-name';
import { agreementTaxLabel } from '../agreement-tax-label';

@Component({
  selector: 'app-agreement-template-alberta',
  templateUrl: './agreement-template-alberta.component.html',
  styleUrls: ['./agreement-template-alberta.component.css']
})
export class AgreementTemplateAlbertaComponent {
  @Input() entityData: any;
  @Input() studentAccountEntityData: any;
  @Input() studioInformationConfig: any;
  @Input() enrollmentForm: any;
  @Input() isTaxInclusive = false;
  /** Studio tax configuration rows; drives the tax column label (falls back to the paper form's "GST"). */
  @Input() taxConfig: any[] = [];
  @Input() displaySignatures = true;
  @Input() displaySigned = true;
  @Input() _enrollmentEntityData: any;

  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) return null;
    if (Array.isArray(contacts)) {
      const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
      return active[0] || contacts[0] || null;
    }
    return contacts;
  }

  get taxLabel(): string {
    return agreementTaxLabel(this.taxConfig, 'GST');
  }

  /**
   * Total the student owes for this enrollment. field_enrollment_total_price is
   * the pre-tax total (lessons - discount) in both tax modes; field_taxes is the
   * tax amount. When the studio's taxes are exclusive the tax sits ON TOP of the
   * price and must be added; when inclusive it is already inside the price.
   */
  calculateTotalTuition(): number {
    const price = parseFloat(this.entityData?.field_enrollment_total_price) || 0;
    return this.isTaxInclusive ? price : price + this.getTaxAmount();
  }

  /** Enrollment-level tax amount (field_taxes), regardless of tax mode. */
  getTaxAmount(): number {
    return parseFloat(this.entityData?.field_taxes) || 0;
  }

  /** Per-payment amount actually payable: adds the row's tax when taxes are exclusive. */
  private payableAmount(row: any): number {
    const amount = parseFloat(this.fieldValue(row, 'field_payment_amount')) || 0;
    if (this.isTaxInclusive) return amount;
    return amount + (parseFloat(this.fieldValue(row, 'field_tax_amount')) || 0);
  }

  /**
   * Tuition before discount and tax. Lesson packages = lesson price x count;
   * zero-lesson enrollments (Miscellaneous, Sundry, services) price the whole
   * thing in field_enrollment_lesson_price / field_total_lessons_price, so fall
   * back to those rather than printing $0.00 tuition.
   */
  getOriginalPackageCost(): number {
    const price = parseFloat(this.entityData?.field_enrollment_lesson_price) || 0;
    const count = parseFloat(this.entityData?.field_enrollment_lesson_count) || 0;
    if (price > 0 && count > 0) {
      return price * count;
    }
    const gross = parseFloat(this.entityData?.field_total_lessons_price) || 0;
    if (gross > 0) return gross;
    if (price > 0) return price;
    // Last resort: net price plus whatever discount was taken off it.
    const net = parseFloat(this.entityData?.field_enrollment_total_price) || 0;
    const discount = typeof (this as any).getDiscountAmount === 'function'
      ? ((this as any).getDiscountAmount() || 0)
      : (parseFloat(this.entityData?.field_discount) || 0);
    return net > 0 ? net + (this.entityData?.field_discount_type === 'percentage' ? 0 : discount) : 0;
  }

  getDiscountAmount(): number {
    const discount = parseFloat(this.entityData?.field_discount) || 0;
    if (this.entityData?.field_discount_type === 'percentage') {
      return this.getOriginalPackageCost() * (discount / 100);
    }
    return discount;
  }

  /** Pre-tax subtotal: strips the included tax portion when taxes are inclusive. */
  getSubtotal(): number {
    const net = this.getOriginalPackageCost() - this.getDiscountAmount();
    return this.isTaxInclusive ? net - this.getTaxAmount() : net;
  }

  getGstAmount(): number {
    return parseFloat(this.entityData?.field_taxes) || 0;
  }

  getPackageName(): string {
    return agreementPackageName(this.entityData?.field_enrollment_package_name);
  }

  /** Lessons purchased on this enrollment ('' for zero-lesson services so the line stays blank). */
  getLessonCount(): string {
    const count = this.fieldValue(this.entityData, 'field_enrollment_lesson_count');
    return count !== undefined && count !== null && count !== '' ? String(count) : '';
  }

  /** Enrollment notes (field_notes) — saved entity, form draft and preview mock shapes. */
  getNotes(): string {
    const notes = this.entityData?.field_notes;
    if (!notes) return '';
    if (typeof notes === 'string') return notes;
    if (Array.isArray(notes)) return notes[0]?.value || '';
    if (typeof notes === 'object' && 'value' in notes) return notes.value || '';
    return '';
  }

  getOutstandingBalance(): number {
    return parseFloat(this.entityData?.field_outstanding_balance_pc) || 0;
  }

  getConsolidatedCashPrice(): number {
    return this.calculateTotalTuition() + this.getOutstandingBalance();
  }

  getBalanceToFinance(): number {
    return this.getConsolidatedCashPrice() - this.getDownPaymentAmount();
  }

  get enrollmentTypeState() {
    const category = this.entityData?.field_category;
    const targetId = Array.isArray(category) ? category[0]?.target_id?.toString() : category?.target_id?.toString() || category?.toString();

    const map: { [key: string]: string } = {
      '50': 'original', '49': 'original',
      '51': 'extension', '543': 'extension', '546': 'extension',
      '65': 'renewal',
      '5': 'service', '66': 'service', '67': 'service', '68': 'service',
      '69': 'service', '542': 'service', '1373': 'service', '1395': 'service',
      '1421': 'original', '70': 'original'
    };

    const mapped = targetId ? map[targetId] : null;
    return {
      original: mapped === 'original',
      extension: mapped === 'extension',
      renewal: mapped === 'renewal',
      service: mapped === 'service' || mapped === null
    };
  }

  /** Extension/renewal enrollments use the financed HT-AM 400-B Alberta form. */
  get isExtensionOrRenewal(): boolean {
    const state = this.enrollmentTypeState;
    return state.extension || state.renewal;
  }

  get isInstallmentAgreement(): boolean {
    const structure = this.enrollmentForm?.get?.('field_payments_structure')?.value
      || this.entityData?.field_payments_structure;
    return structure && structure !== 'paid_in_full';
  }

  /** Scheduled payment rows normalized to {amount, date, down}, date ascending. */
  private scheduledPaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    let raw = agreementScheduledPayments(this.enrollmentForm?.get?.('field_scheduled_payments')?.value);
    if (raw.length === 0) {
      raw = agreementScheduledPayments(this.entityData?.field_scheduled_payments);
    }

    return raw
      .map(r => ({
        amount: this.payableAmount(r),
        date: this.fieldValue(r, 'field_payment_date') || null,
        down: ['1', 1, true].includes(this.fieldValue(r, 'field_down_payment'))
      }))
      .filter(r => r.amount > 0)
      .sort((x, y) => !x.date ? 1 : !y.date ? -1 : x.date.localeCompare(y.date));
  }

  getDownPaymentAmount(): number {
    const down = this.scheduledPaymentRows().find(r => r.down);
    return down?.amount || 0;
  }

  /** All scheduled payments for the payment table (US-style dual mode). */
  get allPaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    return this.scheduledPaymentRows();
  }

  areAllPaymentAmountsSame(): boolean {
    const rows = this.scheduledPaymentRows();
    if (rows.length <= 1) return true;
    return rows.every(r => Math.abs(r.amount - rows[0].amount) < 0.01);
  }

  get firstPaymentDate(): string | null {
    return this.scheduledPaymentRows()[0]?.date || null;
  }

  get expirationDate(): any {
    const formVal = this.enrollmentForm?.get?.('field_expiration_date')?.value;
    const v = (formVal !== undefined && formVal !== null && formVal !== '')
      ? formVal
      : this.fieldValue(this.entityData, 'field_expiration_date');
    if (v && typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    return v || null;
  }

  private fieldValue(obj: any, fieldName: string): any {
    const v = obj?.[fieldName];
    if (Array.isArray(v)) return v[0]?.value ?? v[0];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  }
}
